import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import Hls from 'hls.js';
import flvjs from 'flv.js';
import PhoneInputLib from 'react-phone-number-input';
import { isValidPhoneNumber, getCountryCallingCode } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

const API_BASE = process.env.REACT_APP_API_URL
  || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? `http://${window.location.hostname}:3001`
    : 'https://liveshop-api.sendevit.com');
const API = `${API_BASE}/api`;
const SOCKET_URL = API_BASE;

// ─── AXIOS JWT INTERCEPTOR ────────────────────────────────────────────────────
axios.interceptors.request.use(config => {
  const token = JSON.parse(localStorage.getItem('ls_user') || 'null')?.token;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// ─── MATERIAL SYMBOL HELPER ───────────────────────────────────────────────────
function Icon({ name, size = 22, fill = 0, style: s = {} }) {
  return (
    <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: `'FILL' ${fill}, 'wght' 400, 'GRAD' 0, 'opsz' ${size}`, lineHeight: 1, verticalAlign: 'middle', ...s }}>
      {name}
    </span>
  );
}

// ─── UTILS ────────────────────────────────────────────────────────────────────
function formatPrice(p) {
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(p);
}

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const ts = typeof expiresAt === 'number' ? expiresAt : new Date(expiresAt).getTime();
    const tick = () => setRemaining(Math.max(0, ts - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return { remaining, mins, secs, expired: remaining === 0 };
}

function getInitials(nom) {
  const parts = nom.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nom.slice(0, 2).toUpperCase();
}

function getAvatarColor(nom) {
  const colors = ['#ff6b6b', '#ffa94d', '#ffd43b', '#69db7c', '#4dabf7', '#748ffc', '#da77f2', '#f783ac'];
  let hash = 0;
  for (let i = 0; i < nom.length; i++) hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function useRoute() {
  const params = new URLSearchParams(window.location.search);
  return {
    liveVendorId:  params.get('live'),
    deliveryToken: params.get('delivery'),
    payToken:      params.get('pay'),
  };
}

// ─── ADDRESS DATA ──────────────────────────────────────────────────────────────
const SENEGAL_CITIES = [
  'Dakar','Pikine','Guédiawaye','Rufisque','Thiès','M\'bour','Saint-Louis',
  'Kaolack','Ziguinchor','Diourbel','Louga','Tambacounda','Kolda','Fatick',
  'Matam','Kaffrine','Kédougou','Sédhiou','Touba','Tivaouane','Bargny','Saly',
];
const QUARTIERS_BY_CITY = {
  'Dakar':       ['Plateau','Médina','Gueule Tapée','Fann','Point E','Sacré-Cœur','Liberté 1-6','Mermoz','Ouakam','Almadies','Ngor','Yoff','Grand Yoff','HLM','Parcelles Assainies','Patte d\'Oie','Cambérène','Hann Bel Air','Biscuiterie','Rebeuss'],
  'Pikine':      ['Pikine Centre','Thiaroye','Guinaw Rails','Djidah Thiaroye','Yeumbeul','Keur Massar','Malika','Diamaguène'],
  'Guédiawaye':  ['Sam Notaire','Golf','Médina Gounass','Wakhinane','Cité Aliou Sow'],
  'Rufisque':    ['Rufisque Centre','Diokoul','Colobane','Bargny','Sangalkam'],
  'Thiès':       ['Thiès Centre','Randoulène','Mbambara','Cité Lamy','Nguinth'],
  'Saint-Louis': ['Saint-Louis Centre','Sor','Léona','Guet Ndar','Pikine St-Louis'],
  'Kaolack':     ['Kaolack Centre','Médina Mbaba','Sara','Sing Sing Léona'],
};

function AddressInput({ value, onChange }) {
  const [villeSearch, setVilleSearch] = useState(value?.ville || '');
  const [showVille, setShowVille] = useState(false);
  const [quartSearch, setQuartSearch] = useState(value?.quartier || '');
  const [showQuart, setShowQuart] = useState(false);

  const filteredVilles = SENEGAL_CITIES.filter(c => c.toLowerCase().includes(villeSearch.toLowerCase()));
  const suggestions = QUARTIERS_BY_CITY[value?.ville] || [];
  const filteredQuarts = suggestions.filter(q => q.toLowerCase().includes(quartSearch.toLowerCase()));

  const selectVille = (v) => {
    setVilleSearch(v); setShowVille(false);
    onChange({ ...value, ville: v, quartier: '' });
    setQuartSearch('');
  };
  const selectQuart = (q) => {
    setQuartSearch(q); setShowQuart(false);
    onChange({ ...value, quartier: q });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ position: 'relative' }}>
        <input style={styles.input} placeholder="Ville (ex: Dakar, Thiès…)" value={villeSearch}
          onChange={e => { setVilleSearch(e.target.value); setShowVille(true); onChange({ ...value, ville: e.target.value }); }}
          onFocus={() => setShowVille(true)} onBlur={() => setTimeout(() => setShowVille(false), 150)} />
        {showVille && filteredVilles.length > 0 && (
          <div style={styles.catDropdown}>
            {filteredVilles.map(c => (
              <div key={c} style={styles.catOption} onMouseDown={() => selectVille(c)}
                onMouseEnter={e => e.currentTarget.style.background='#f2f2f7'} onMouseLeave={e => e.currentTarget.style.background=''}>{c}</div>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: 'relative' }}>
        <input style={styles.input} placeholder="Quartier / Zone…" value={quartSearch}
          onChange={e => { setQuartSearch(e.target.value); setShowQuart(true); onChange({ ...value, quartier: e.target.value }); }}
          onFocus={() => setShowQuart(true)} onBlur={() => setTimeout(() => setShowQuart(false), 150)} />
        {showQuart && filteredQuarts.length > 0 && (
          <div style={styles.catDropdown}>
            {filteredQuarts.map(q => (
              <div key={q} style={styles.catOption} onMouseDown={() => selectQuart(q)}
                onMouseEnter={e => e.currentTarget.style.background='#f2f2f7'} onMouseLeave={e => e.currentTarget.style.background=''}>{q}</div>
            ))}
          </div>
        )}
      </div>
      <input style={styles.input} placeholder="Précisions (bâtiment, point de repère…)"
        value={value?.details || ''} onChange={e => onChange({ ...value, details: e.target.value })} />
    </div>
  );
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
const toastListeners = new Set();
function toast(msg, type = 'error') { toastListeners.forEach(fn => fn(msg, type)); }

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const fn = (msg, type) => {
      const id = Date.now() + Math.random();
      setToasts(prev => [...prev, { id, msg, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };
    toastListeners.add(fn);
    return () => toastListeners.delete(fn);
  }, []);
  if (!toasts.length) return null;
  const palette = {
    error:   { bg: '#fff0f0', color: '#cc0000', border: '#ffc0c0', icon: 'cancel' },
    success: { bg: '#e8f9ee', color: '#1a7a3a', border: '#b0e8c0', icon: 'check_circle' },
    warning: { bg: '#fff8e6', color: '#7a4f00', border: '#ffd88a', icon: 'warning' },
    info:    { bg: '#eef4ff', color: '#1a3a7a', border: '#b0ccf5', icon: 'info' },
  };
  return (
    <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', pointerEvents: 'none' }}>
      {toasts.map(t => {
        const c = palette[t.type] || palette.info;
        return (
          <div key={t.id} style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, padding: '13px 22px', borderRadius: 14, fontWeight: 600, fontSize: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.10)', animation: 'slideIn 0.3s ease', maxWidth: 380, textAlign: 'center', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name={c.icon} size={18} fill={1} /> {t.msg}
          </div>
        );
      })}
    </div>
  );
}

// ─── TIMER ────────────────────────────────────────────────────────────────────
function PendingTimer({ expiresAt }) {
  const { mins, secs, expired } = useCountdown(expiresAt);
  if (expired) return <div style={{ fontSize: 11, color: '#ff3b30', fontWeight: 700 }}>Expiré</div>;
  return <div style={{ fontSize: 11, color: '#ff9f0a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}><Icon name="timer" size={13} /> {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}</div>;
}

function Timer({ expiresAt, onExpired }) {
  const { remaining, mins, secs, expired } = useCountdown(expiresAt);
  useEffect(() => { if (expired && onExpired) onExpired(); }, [expired]);
  const urgent = remaining < 120000;
  return (
    <div style={{ ...styles.timer, background: urgent ? '#ff3b30' : '#e5f5eb' }}>
      <span style={{ ...styles.timerLabel, color: urgent ? '#fff' : '#1d1d1f', display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="timer" size={16} /> Temps restant</span>
      <span style={{ ...styles.timerValue, color: urgent ? '#fff' : '#1d1d1f' }}>{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span>
    </div>
  );
}

// ─── USER AVATAR ──────────────────────────────────────────────────────────────
function UserAvatar({ user, walletBalance, onClick }) {
  return (
    <button style={styles.avatarBtn} onClick={onClick} title="Mon profil">
      {walletBalance > 0 && <div style={styles.avatarBadge}>{formatPrice(walletBalance)}</div>}
      <div style={{ ...styles.avatarCircle, background: getAvatarColor(user.nom) }}>
        {getInitials(user.nom)}
      </div>
    </button>
  );
}

// ─── PROFILE PANEL ────────────────────────────────────────────────────────────
function ProfilePanel({ user, walletBalance, onClose, onWalletUpdate, onSettingsUpdate }) {
  const [activeTab, setActiveTab] = useState('wallet');
  const [address, setAddress] = useState({ quartier: '', ville: '', details: '' });
  const [savedAddress, setSavedAddress] = useState(null);
  const [settings, setSettings] = useState({ directPayment: false });
  const [balance, setBalance] = useState(walletBalance);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupMethod, setTopupMethod] = useState('wave');
  const [topupCheckout, setTopupCheckout] = useState(null);
  const [topupPolling, setTopupPolling] = useState(false);
  const topupPollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [shopModalVendeurId, setShopModalVendeurId] = useState(null);

  useEffect(() => { setBalance(walletBalance); }, [walletBalance]);
  useEffect(() => () => { if (topupPollRef.current) clearInterval(topupPollRef.current); }, []);

  useEffect(() => {
    axios.get(`${API}/users/${user.userId}`).then(r => {
      setBalance(r.data.walletBalance || 0);
      if (r.data.address) { setSavedAddress(r.data.address); setAddress(r.data.address); }
      if (r.data.settings) setSettings(r.data.settings);
    }).catch(() => {});
  }, [user.userId]);

  const saveAddress = async () => {
    if (!address.quartier || !address.ville) return;
    setLoading(true);
    try {
      await axios.post(`${API}/users/${user.userId}/address`, { address });
      setSavedAddress({ ...address });
    } catch (e) { toast('Erreur lors de la sauvegarde de l\'adresse'); }
    setLoading(false);
  };

  const toggleDirectPayment = async (val) => {
    const newSettings = { ...settings, directPayment: val };
    setSettings(newSettings);
    try {
      await axios.post(`${API}/users/${user.userId}/settings`, newSettings);
      onSettingsUpdate(newSettings);
    } catch (e) {}
  };

  const stopTopupPolling = () => {
    if (topupPollRef.current) { clearInterval(topupPollRef.current); topupPollRef.current = null; }
    setTopupPolling(false);
  };

  const startTopupPolling = (commandRef) => {
    setTopupPolling(true);
    topupPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/payment/transaction/${commandRef}`);
        const s = res.data.status;
        if (s === 'success') {
          stopTopupPolling();
          const u = await axios.get(`${API}/users/${user.userId}`);
          const newBal = u.data.walletBalance || 0;
          setBalance(newBal);
          onWalletUpdate(newBal);
          setTopupCheckout(null);
          setTopupAmount('');
          toast('Recharge effectuée avec succès !', 'success');
        } else if (s === 'failed' || s === 'cancelled') {
          stopTopupPolling();
          setTopupCheckout(null);
          toast('Paiement échoué ou annulé. Veuillez réessayer.', 'error');
        }
      } catch {}
    }, 3000);
  };

  const initiateTopup = async () => {
    const amount = parseInt(topupAmount);
    if (!amount || amount < 100) { toast('Montant minimum : 100 XOF', 'warning'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/users/${user.userId}/payment/topup`, {
        amount,
        service: KOPAR_SERVICES[topupMethod],
      });
      const { commandRef, paymentUrl, qrCode, checkoutMsg } = res.data;
      setTopupCheckout({ commandRef, paymentUrl, qrCode, checkoutMsg });
      startTopupPolling(commandRef);
    } catch (e) { toast(e.response?.data?.error || 'Erreur lors de la recharge'); }
    setLoading(false);
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${API}/users/${user.userId}/orders`);
      setUserOrders(res.data);
    } catch {}
    setLoadingOrders(false);
  };

  const changeOrderMethod = async (orderId, method) => {
    try {
      await axios.put(`${API}/orders/${orderId}/delivery-method`, { method });
      setUserOrders(prev => prev.map(o => o.id === orderId ? { ...o, deliveryMethod: method } : o));
      toast('Méthode de livraison mise à jour', 'success');
    } catch { toast('Erreur lors de la mise à jour'); }
  };

  const tabs = [
    { id: 'wallet',     icon: 'account_balance_wallet', label: 'Portefeuille' },
    { id: 'commandes',  icon: 'inventory_2',            label: 'Commandes' },
    { id: 'livraison',  icon: 'location_on',            label: 'Livraison' },
    { id: 'parametres', icon: 'settings',               label: 'Paramètres' },
  ];

  return (
    <>
      <div style={styles.profileOverlay} onClick={onClose} />
      <div style={styles.profilePanel}>
        <div style={styles.profileHeader}>
          <div style={{ ...styles.avatarCircle, width: 52, height: 52, fontSize: 20, background: getAvatarColor(user.nom), flexShrink: 0 }}>
            {getInitials(user.nom)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.profileName}>{user.nom}</div>
            <div style={styles.profilePhone}>{user.phone}</div>
          </div>
          <button style={styles.profileClose} onClick={onClose}><Icon name="close" size={20} /></button>
        </div>
        <div style={{ ...styles.profileTabs, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} style={{ ...styles.profileTab, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5, ...(activeTab === t.id ? styles.profileTabActive : {}) }}
              onClick={() => { setActiveTab(t.id); if (t.id === 'commandes' && userOrders.length === 0) loadOrders(); }}>
              <Icon name={t.icon} size={16} /> {t.label}
            </button>
          ))}
        </div>
        <div style={styles.profileContent}>
          {activeTab === 'wallet' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={styles.walletCard}>
                <div style={styles.walletLabel}>Solde disponible</div>
                <div style={styles.walletAmount}>{formatPrice(balance)}</div>
                {balance > 0 && <div style={styles.walletHint}>Utilisable pour payer vos achats</div>}
              </div>
              <div style={styles.sectionTitle}>Recharger mon compte</div>
              <input style={styles.input} placeholder="Montant à recharger (XOF)" type="number" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} />
              <div style={styles.payMethods}>
                {['wave', 'orange'].map(m => (
                  <button key={m} style={{ ...styles.payMethodBtn, ...(topupMethod === m ? styles.payMethodActive : {}) }} onClick={() => setTopupMethod(m)}>
                    <Icon name="waves" size={16} style={{ color: m === 'wave' ? '#0a84ff' : '#ff6a00' }} /> {m === 'wave' ? 'Wave' : 'Orange Money'}
                  </button>
                ))}
              </div>
              {!topupCheckout ? (
                <button style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }} onClick={initiateTopup} disabled={loading || !topupAmount}>
                  {loading ? '...' : `Recharger ${topupAmount ? formatPrice(parseInt(topupAmount) || 0) : ''}`}
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {topupCheckout.qrCode && (
                    <div style={{ textAlign: 'center' }}>
                      <img src={topupCheckout.qrCode} alt="QR Code de paiement"
                        style={{ width: 160, height: 160, borderRadius: 12, border: '2px solid #e5e5ea' }} />
                      <div style={{ fontSize: 12, color: '#8e8e93', marginTop: 6 }}>
                        Scannez avec votre application {topupMethod === 'wave' ? 'Wave' : 'Orange Money'}
                      </div>
                    </div>
                  )}
                  {topupCheckout.paymentUrl && (
                    <a href={topupCheckout.paymentUrl} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, background: topupMethod === 'wave' ? '#0a84ff' : '#ff6a00', color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
                      <Icon name="open_in_new" size={16} style={{ color: '#fff' }} />
                      Payer via {topupMethod === 'wave' ? 'Wave' : 'Orange Money'}
                    </a>
                  )}
                  {topupCheckout.checkoutMsg && (
                    <div style={{ fontSize: 13, color: '#6e6e73', textAlign: 'center' }}>{topupCheckout.checkoutMsg}</div>
                  )}
                  {topupPolling && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#8e8e93', fontSize: 13 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff9f0a', animation: 'liveDot 1.2s ease-in-out infinite', flexShrink: 0 }} />
                      En attente de confirmation du paiement...
                    </div>
                  )}
                  <button style={styles.btnCancel} onClick={() => { stopTopupPolling(); setTopupCheckout(null); }}>Annuler</button>
                </div>
              )}
            </div>
          )}
          {activeTab === 'commandes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={styles.sectionTitle}>Mes commandes</div>
              {loadingOrders ? (
                <div style={{ color: '#8e8e93', textAlign: 'center', padding: '24px 0' }}>Chargement...</div>
              ) : userOrders.length === 0 ? (
                <div style={{ color: '#8e8e93', textAlign: 'center', padding: '24px 0', fontSize: 14 }}>Aucune commande pour l'instant</div>
              ) : userOrders.map(o => {
                const orderBadge = {
                  pending_payment: { label: <><Icon name="schedule" size={12} /> En attente de paiement</>, bg: '#fff8e6', color: '#7a4f00' },
                  paid:            { label: <><Icon name="check_circle" size={12} fill={1} /> Payé</>, bg: '#e8f9ee', color: '#1a7a3a' },
                  expired:         { label: <><Icon name="cancel" size={12} fill={1} /> Expiré</>, bg: '#fff0f0', color: '#cc0000' },
                }[o.status] || { label: o.status, bg: '#f2f2f7', color: '#6e6e73' };
                const dStatus = {
                  pending_delivery: { label: <><Icon name="hourglass_empty" size={12} /> À livrer</>, bg: '#f2f2f7', color: '#6e6e73' },
                  in_transit:       { label: <><Icon name="local_shipping" size={12} /> En cours</>, bg: '#fff8e6', color: '#7a4f00' },
                  delivered:        { label: <><Icon name="check_circle" size={12} fill={1} /> Livré</>, bg: '#e8f9ee', color: '#1a7a3a' },
                }[o.deliveryStatus];
                return (
                  <div key={o.id} style={{ background: '#f9f9fb', borderRadius: 14, padding: '14px', border: '1px solid #e5e5ea' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1d1d1f' }}>{o.productName}</div>
                    <div style={{ fontSize: 13, color: '#8e8e93', marginBottom: 6 }}>
                      {o.shopName}
                      {o.vendeurId && <button style={{ marginLeft: 8, background: 'none', border: 'none', color: '#0a84ff', fontSize: 12, cursor: 'pointer', padding: 0 }} onClick={() => setShopModalVendeurId(o.vendeurId)}>Voir la boutique →</button>}
                    </div>
                    <div style={{ color: '#30c759', fontWeight: 800, fontSize: 18, marginBottom: 10 }}>{formatPrice(o.prix)}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: orderBadge.bg, color: orderBadge.color }}>{orderBadge.label}</span>
                      {dStatus && <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: dStatus.bg, color: dStatus.color }}>{dStatus.label}</span>}
                      {o.status === 'paid' && (
                        <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#f2f2f7', color: '#6e6e73' }}>
                          {o.deliveryMethod === 'pickup' ? <><Icon name="storefront" size={12} /> Retrait</> : <><Icon name="home" size={12} /> Domicile</>}
                        </span>
                      )}
                      {o.status === 'paid' && o.deliveryStatus !== 'delivered' && (
                        <button style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #d2d2d7', background: '#fff', color: '#0a84ff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => changeOrderMethod(o.id, o.deliveryMethod === 'delivery' ? 'pickup' : 'delivery')}>
                          → {o.deliveryMethod === 'delivery' ? 'Retrait' : 'Domicile'}
                        </button>
                      )}
                    </div>
                    {o.deliveryCode && o.deliveryStatus === 'in_transit' && (
                      <div style={{ marginTop: 10, background: '#f0f7ff', border: '1px solid #b3d4ff', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon name="pin" fill={1} size={20} style={{ color: '#0a84ff' }} />
                        <div>
                          <div style={{ fontSize: 11, color: '#0a84ff', fontWeight: 700, textTransform: 'uppercase' }}>Code de livraison</div>
                          <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 6, color: '#0a84ff', lineHeight: 1.2 }}>{o.deliveryCode}</div>
                          <div style={{ fontSize: 11, color: '#6e6e73', marginTop: 2 }}>Communiquez ce code au livreur</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {activeTab === 'livraison' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={styles.sectionTitle}>Adresse de livraison</div>
              {savedAddress && (
                <div style={styles.savedAddress}>
                  <div style={{ fontWeight: 600, color: '#1d1d1f', display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="location_on" size={15} /> {savedAddress.quartier}, {savedAddress.ville}</div>
                  {savedAddress.details && <div style={{ color: '#6e6e73', fontSize: 13, marginTop: 4 }}>{savedAddress.details}</div>}
                </div>
              )}
              <AddressInput value={address} onChange={setAddress} />
              <button style={{ ...styles.btnPrimary, opacity: (loading || !address.quartier || !address.ville) ? 0.6 : 1 }} onClick={saveAddress} disabled={loading || !address.quartier || !address.ville}>
                {loading ? '...' : savedAddress ? <><Icon name="edit" size={15} /> Modifier l'adresse</> : <><Icon name="location_on" size={15} /> Enregistrer l'adresse</>}
              </button>
            </div>
          )}
          {activeTab === 'parametres' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={styles.sectionTitle}>Règles de paiement</div>
              <div style={styles.settingRow}>
                <div style={styles.settingInfo}>
                  <div style={{ ...styles.settingTitle, display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="bolt" size={16} fill={1} /> Paiement direct</div>
                  <div style={styles.settingDesc}>Si mon solde est suffisant, payer automatiquement dès que je clique sur "Acheter"</div>
                </div>
                <button style={{ ...styles.toggle, background: settings.directPayment ? '#30c759' : '#d2d2d7' }} onClick={() => toggleDirectPayment(!settings.directPayment)}>
                  <div style={{ ...styles.toggleThumb, transform: settings.directPayment ? 'translateX(22px)' : 'translateX(2px)' }} />
                </button>
              </div>
              {settings.directPayment && <div style={{ ...styles.settingNote, display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="check_circle" size={15} fill={1} /> Activé — Solde actuel : <strong>{formatPrice(balance)}</strong></div>}
              <div style={{ borderTop: '1px solid #e5e5ea', paddingTop: 16 }}>
                <button style={{ ...styles.btnDanger, width: '100%', fontSize: 14 }} onClick={() => { localStorage.removeItem('ls_user'); window.location.reload(); }}>
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {shopModalVendeurId && <ShopInfoModal vendeurId={shopModalVendeurId} onClose={() => setShopModalVendeurId(null)} />}
    </>
  );
}

// ─── PHONE INPUT ──────────────────────────────────────────────────────────────
const _phoneInputStyleId = 'liveshop-phone-input-style';
if (!document.getElementById(_phoneInputStyleId)) {
  const s = document.createElement('style');
  s.id = _phoneInputStyleId;
  s.textContent = `.PhoneInput { display: flex; align-items: center; width: 100%; padding: 0 8px 0 4px; box-sizing: border-box; } .PhoneInputInput { flex: 1; border: none; background: transparent; outline: none; font-size: 15px; color: #1d1d1f; padding: 13px 8px; min-width: 0; }`;
  document.head.appendChild(s);
}

function CountrySelect({ value, onChange, options, iconComponent: FlagIcon }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = (options || []).filter(o =>
    o.value && o.label?.toLowerCase().includes(search.toLowerCase())
  );

  const safeCallingCode = (code) => {
    try { return '+' + getCountryCallingCode(code); } catch { return ''; }
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: '10px 6px 10px 10px' }}
      >
        {FlagIcon && <FlagIcon country={value} label={value || ''} />}
        <Icon name="arrow_drop_down" size={16} style={{ color: '#8e8e93' }} />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid #d2d2d7', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 99999, width: 290, maxHeight: 340, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f2f2f7' }}>
            <input
              autoFocus
              style={{ width: '100%', border: '1px solid #d2d2d7', borderRadius: 8, padding: '7px 10px', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f9f9f9' }}
              placeholder="Rechercher un pays..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#8e8e93', fontSize: 14 }}>Aucun résultat</div>
            ) : filtered.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: o.value === value ? '#f2f2f7' : 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 14, color: '#1d1d1f' }}
              >
                {FlagIcon && <FlagIcon country={o.value} label={o.label} />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                <span style={{ color: '#8e8e93', fontSize: 13, flexShrink: 0 }}>{safeCallingCode(o.value)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Cache module-level : un seul appel réseau pour toute la session
let _ipCountryCache = null;
let _ipCountryPromise = null;
function detectCountryByIp() {
  if (_ipCountryCache) return Promise.resolve(_ipCountryCache);
  if (!_ipCountryPromise) {
    _ipCountryPromise = fetch('https://ipwho.is/')
      .then(r => r.json())
      .then(d => {
        const code = d?.country_code?.toUpperCase() || null;
        _ipCountryCache = code;
        return code;
      })
      .catch(() => { _ipCountryCache = null; return null; });
  }
  return _ipCountryPromise;
}

function PhoneInput({ value, onChange, placeholder, error }) {
  const [country, setCountry] = useState(null); // null = en cours de détection

  useEffect(() => {
    // Fallback 'SN' : plateforme West Africa, ne pas utiliser navigator.language
    // qui retourne souvent 'fr-FR' → France pour les francophones
    detectCountryByIp().then(c => setCountry(c || 'SN'));
  }, []);

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...styles.input, padding: 0, display: 'flex', alignItems: 'center', overflow: 'visible', marginBottom: 0 }}>
        {country ? (
          <PhoneInputLib
            key={country}
            international
            defaultCountry={country}
            value={value}
            onChange={onChange}
            placeholder={placeholder || 'Numéro de téléphone'}
            countrySelectComponent={CountrySelect}
          />
        ) : (
          <div style={{ flex: 1, padding: '13px 14px', color: '#8e8e93', fontSize: 14 }}>Détection du pays…</div>
        )}
      </div>
      {error && <div style={{ color: '#ff3b30', fontSize: 12, marginTop: 4, paddingLeft: 4 }}>{error}</div>}
    </div>
  );
}

// ─── REGISTER MODAL (client) ──────────────────────────────────────────────────
function RegisterModal({ onRegister }) {
  const [nom, setNom] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  const submitStep1 = async () => {
    if (!nom.trim()) { toast('Entrez votre nom', 'warning'); return; }
    if (!phone || !isValidPhoneNumber(phone)) { setPhoneError('Numéro de téléphone invalide'); return; }
    setPhoneError('');
    setLoading(true);
    try {
      await axios.post(`${API}/users/register`, { nom: nom.trim(), phone });
      setOtpStep(true);
    } catch (e) { toast(e.response?.data?.error || 'Erreur lors de l\'inscription'); }
    setLoading(false);
  };

  const submitStep2 = async () => {
    if (!otpCode || otpCode.length !== 6) { toast('Entrez le code à 6 chiffres', 'warning'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/users/register/verify`, { phone, otp: otpCode });
      localStorage.setItem('ls_user', JSON.stringify(res.data));
      onRegister(res.data);
    } catch (e) { toast(e.response?.data?.error || 'Code OTP invalide ou expiré'); }
    setLoading(false);
  };

  if (otpStep) return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalLogo}><Icon name="sms" size={38} fill={1} style={{ color: '#0a84ff' }} /></div>
        <h2 style={styles.modalTitle}>Vérification</h2>
        <p style={styles.modalSub}>Un code à 6 chiffres a été envoyé au <strong>{phone}</strong></p>
        <input style={{ ...styles.input, letterSpacing: 6, textAlign: 'center', fontSize: 22, fontWeight: 700 }}
          placeholder="000000" maxLength={6} inputMode="numeric"
          value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={e => e.key === 'Enter' && submitStep2()} />
        <button style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }} onClick={submitStep2} disabled={loading}>
          {loading ? 'Vérification...' : 'Rejoindre le live →'}
        </button>
        <button style={styles.btnLink} onClick={() => { setOtpStep(false); setOtpCode(''); }}>← Modifier mon numéro</button>
      </div>
    </div>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalLogo}><Icon name="shopping_bag" size={38} fill={1} style={{ color: '#0a84ff' }} /></div>
        <h2 style={styles.modalTitle}>Rejoindre le Live</h2>
        <p style={styles.modalSub}>Entrez vos infos pour acheter en temps réel</p>
        <input style={styles.input} placeholder="Votre prénom et nom" value={nom} onChange={e => setNom(e.target.value)} />
        <PhoneInput value={phone} onChange={v => { setPhone(v || ''); setPhoneError(''); }} placeholder="Numéro Wave / Orange Money" error={phoneError} />
        <button style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }} onClick={submitStep1} disabled={loading}>
          {loading ? 'Envoi du code...' : 'Recevoir mon code →'}
        </button>
      </div>
    </div>
  );
}

// ─── SHOP INFO MODAL ──────────────────────────────────────────────────────────
function ShopInfoModal({ vendeurId, onClose }) {
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vendors/${vendeurId}/public`)
      .then(r => setShop(r.data))
      .catch(() => setShop(null))
      .finally(() => setLoading(false));
  }, [vendeurId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', width: '100%', maxWidth: 480, boxShadow: '0 -4px 30px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, background: '#d2d2d7', borderRadius: 2, margin: '0 auto 20px' }} />
        {loading ? (
          <div style={{ textAlign: 'center', color: '#8e8e93', padding: '20px 0' }}>Chargement...</div>
        ) : !shop ? (
          <div style={{ textAlign: 'center', color: '#cc0000', padding: '20px 0' }}>Boutique introuvable</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#0a84ff,#30c759)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                {shop.shopName?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#1d1d1f' }}>{shop.shopName}</div>
                <div style={{ fontSize: 13, color: '#8e8e93' }}>Propriétaire : {shop.nom}</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a href={`tel:${shop.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f2f2f7', borderRadius: 12, padding: '12px 16px', textDecoration: 'none', color: '#0a84ff' }}>
                <Icon name="phone" size={22} fill={1} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{shop.phone}</div>
                  <div style={{ fontSize: 12, color: '#8e8e93' }}>Appuyer pour appeler</div>
                </div>
              </a>

              {shop.methods?.length > 0 && (
                <div style={{ background: '#f2f2f7', borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ fontSize: 12, color: '#8e8e93', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Modes de livraison</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {shop.methods.includes('delivery') && <span style={{ background: '#fff', border: '1px solid #d2d2d7', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="home" size={14} /> Domicile</span>}
                    {shop.methods.includes('pickup')   && <span style={{ background: '#fff', border: '1px solid #d2d2d7', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="storefront" size={14} /> Retrait</span>}
                  </div>
                </div>
              )}

              {shop.pickupInfo && (
                <div style={{ background: '#f2f2f7', borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ fontSize: 12, color: '#8e8e93', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Adresse de retrait</div>
                  <div style={{ fontSize: 14, color: '#1d1d1f', display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="location_on" size={15} /> {shop.pickupInfo}</div>
                </div>
              )}
            </div>

            <button style={{ ...styles.btnPrimary, marginTop: 20, background: 'none', border: '1px solid #d2d2d7', color: '#1d1d1f' }} onClick={onClose}>Fermer</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PRODUCT BOTTOM BAR ───────────────────────────────────────────────────────
function ProductBottomBar({ product, user, vendeurId, onBought, onDirectPaid, myOrder, onShowPayment, paid, walletBalance, userSettings }) {
  const [loading, setLoading] = useState(false);
  const [wishlistDone, setWishlistDone] = useState(false);
  const [waitlistPos, setWaitlistPos] = useState(null);

  const isLocked = product.status === 'locked';
  const isSold = product.status === 'sold';
  const isAvailable = product.status === 'available';
  const isMine = myOrder && product.lockedBy === user?.userId;
  const canDirectPay = userSettings?.directPayment && walletBalance >= product.prix;

  useEffect(() => { setLoading(false); setWishlistDone(false); setWaitlistPos(null); }, [product.id]);

  const handleBuy = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/buy`, { userId: user.userId, productId: product.id, vendeurId });
      const { orderId, expiresAt } = res.data;
      if (canDirectPay) {
        try {
          const walletRes = await axios.post(`${API}/orders/${orderId}/pay-wallet`, { userId: user.userId });
          onDirectPaid(walletRes.data.newBalance);
          return;
        } catch { onBought(orderId, expiresAt); }
      } else {
        onBought(orderId, expiresAt);
      }
    } catch (e) {
      if (e.response?.status === 409) setWaitlistPos(e.response.data.waitlistPosition);
      else toast(e.response?.data?.error || 'Erreur lors de l\'achat');
    }
    setLoading(false);
  };

  const handleWishlist = async () => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/orders/wishlist`, { userId: user.userId, productId: product.id, vendeurId });
      setWishlistDone(true);
      setWaitlistPos(res.data.position);
    } catch (e) { toast('Erreur lors de l\'ajout à la liste d\'attente'); }
  };

  const buyLabel = canDirectPay
    ? <><Icon name="bolt" size={16} fill={1} /> Payer avec mon compte ({formatPrice(walletBalance)})</>
    : loading ? <><Icon name="hourglass_empty" size={16} /> Réservation...</> : <><Icon name="bolt" size={16} fill={1} /> ACHETER MAINTENANT</>;

  return (
    <div style={styles.productBar}>
      {isSold && <div style={{ ...styles.soldBannerBar, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Icon name="check_circle" fill={1} size={16} /> VENDU</div>}
      {isLocked && !isSold && <div style={{ ...styles.lockedBannerBar, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Icon name="lock" size={14} fill={1} /> RÉSERVÉ</div>}
      <div style={styles.productBarContent}>
        <img src={product.photo} alt={product.nom} style={styles.productBarThumb} />
        <div style={styles.productBarName}>{product.nom}</div>
        <div style={styles.productBarPrice}>{formatPrice(product.prix)}</div>
        {isAvailable && !isMine && !isSold && (
          <button style={{ ...styles.btnBuy, opacity: loading ? 0.7 : 1, maxWidth: 340 }} onClick={handleBuy} disabled={loading}>{buyLabel}</button>
        )}
        {isMine && !paid && (
          <button style={{ ...styles.btnPrimary, maxWidth: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={onShowPayment}><Icon name="credit_card" size={16} /> Finaliser mon paiement</button>
        )}
        {isMine && paid && <div style={styles.youWon}><div style={{ ...styles.youWonText, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Icon name="celebration" size={18} fill={1} /> Achat confirmé !</div></div>}
        {isLocked && !isMine && !wishlistDone && !waitlistPos && (
          <button style={{ ...styles.btnWishlist, maxWidth: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={handleWishlist}><Icon name="notifications" size={16} /> M'alerter si disponible</button>
        )}
        {(wishlistDone || waitlistPos) && <div style={{ ...styles.wishlistConfirmBar, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Icon name="check_circle" size={14} fill={1} /> Position #{waitlistPos} — vous serez notifié(e)</div>}
        {isSold && <div style={styles.soldMsg}>Vendu ! Restez connecté pour la suite.</div>}
        {product.waitlistCount > 0 && <div style={{ ...styles.waitlistBadge, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Icon name="group" size={14} /> {product.waitlistCount} personne(s) intéressée(s)</div>}
      </div>
    </div>
  );
}

// ─── PAYMENT MODAL ────────────────────────────────────────────────────────────
const KOPAR_SERVICES = { wave: 'wave_checkout', orange: 'orange_money_sn' };

function PaymentModal({ orderId, product, user, vendeurId, expiresAt, onPaid, onWalletPaid, onClose, walletBalance }) {
  const hasEnoughBalance = walletBalance >= (product?.prix || 0);
  const [method, setMethod] = useState(hasEnoughBalance ? 'wallet' : 'wave');
  const [loading, setLoading] = useState(false);
  // checkout result: { commandRef, paymentUrl?, qrCode?, checkoutMsg? }
  const [checkout, setCheckout] = useState(null);
  const [polling, setPolling] = useState(false);
  const [payError, setPayError] = useState(null);
  const pollRef = useRef(null);
  const { mins, secs, expired } = useCountdown(expiresAt);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  };

  const startPolling = (ref) => {
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/payment/transaction/${ref}`);
        const s = res.data.status;
        if (s === 'success') { stopPolling(); onPaid(); }
        else if (s === 'failed' || s === 'cancelled') {
          stopPolling();
          setPayError('Paiement échoué ou annulé. Veuillez réessayer.');
          setCheckout(null);
        }
      } catch {}
    }, 3000);
  };

  const initMobilePayment = async () => {
    setLoading(true);
    setPayError(null);
    try {
      const res = await axios.post(`${API}/users/${user.userId}/payment/purchase`, {
        amount:      product.prix,
        service:     KOPAR_SERVICES[method],
        orderId,
        productName: product.nom,
        vendeurId,
      });
      const { commandRef, paymentUrl, qrCode, checkoutMsg } = res.data;
      setCheckout({ commandRef, paymentUrl, qrCode, checkoutMsg });
      startPolling(commandRef);
    } catch (e) {
      setPayError(e.response?.data?.error || 'Erreur lors de l\'initiation du paiement');
    }
    setLoading(false);
  };

  const retryPayment = () => { stopPolling(); setCheckout(null); setPayError(null); };

  const payWithWallet = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/pay-wallet`, { userId: user.userId });
      onWalletPaid(res.data.newBalance);
    } catch (e) { toast(e.response?.data?.error || 'Solde insuffisant ou erreur de paiement', 'error'); }
    setLoading(false);
  };

  const methodColor = method === 'wave' ? '#0a84ff' : '#ff6a00';
  const methodLabel = method === 'wave' ? 'Wave' : 'Orange Money';

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: 420 }}>
        <div style={styles.payHeader}>
          <span style={styles.payProduct}>{product?.nom}</span>
          <span style={styles.payPrice}>{formatPrice(product?.prix)}</span>
        </div>
        <div style={{ ...styles.timer, marginBottom: 20 }}>
          <span style={{ ...styles.timerLabel, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="timer" size={16} /> Temps restant</span>
          <span style={{ ...styles.timerValue, color: expired ? '#ff3b30' : '#30c759' }}>
            {expired ? 'EXPIRÉ' : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}
          </span>
        </div>

        {expired ? (
          <div style={styles.expiredMsg}>
            <Icon name="alarm_off" size={18} /> Délai de paiement expiré. La commande a été annulée.
            <button style={{ ...styles.btnPrimary, marginTop: 16 }} onClick={onClose}>Fermer</button>
          </div>

        ) : checkout ? (
          /* ── Paiement initié — afficher les instructions KoparPay ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* QR code si disponible */}
            {checkout.qrCode && (
              <div style={{ textAlign: 'center' }}>
                <img src={checkout.qrCode} alt="QR Code de paiement"
                  style={{ width: 180, height: 180, borderRadius: 12, border: '2px solid #e5e5ea' }} />
                <div style={{ fontSize: 12, color: '#8e8e93', marginTop: 6 }}>
                  Scannez avec votre application {methodLabel}
                </div>
              </div>
            )}

            {/* Lien de paiement si disponible */}
            {checkout.paymentUrl && (
              <a href={checkout.paymentUrl} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 16px', borderRadius: 12, background: methodColor, color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
                <Icon name="open_in_new" size={18} style={{ color: '#fff' }} />
                Payer via {methodLabel}
              </a>
            )}

            {/* Message KoparPay (ex: "Transfert Initié") */}
            {checkout.checkoutMsg && (
              <div style={{ fontSize: 13, color: '#6e6e73', textAlign: 'center' }}>{checkout.checkoutMsg}</div>
            )}

            {/* Indicateur polling */}
            {polling && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#8e8e93', fontSize: 13 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff9f0a', animation: 'liveDot 1.2s ease-in-out infinite', flexShrink: 0 }} />
                En attente de confirmation du paiement...
              </div>
            )}

            <button style={{ ...styles.btnCancel, marginTop: 0 }} onClick={retryPayment}>
              Annuler et réessayer
            </button>
          </div>

        ) : (
          /* ── Choix de méthode + bouton payer ── */
          <>
            <p style={styles.payInstruction}>Choisissez votre méthode de paiement :</p>
            <div style={{ ...styles.payMethods, flexDirection: 'column' }}>
              {hasEnoughBalance && (
                <button style={{ ...styles.payMethodBtn, ...(method === 'wallet' ? styles.payMethodActive : {}), justifyContent: 'space-between', display: 'flex' }}
                  onClick={() => { setMethod('wallet'); setPayError(null); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="account_balance_wallet" size={16} /> Mon compte LiveShop</span>
                  <span style={{ fontWeight: 700 }}>{formatPrice(walletBalance)}</span>
                </button>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                {['wave', 'orange'].map(m => (
                  <button key={m} style={{ ...styles.payMethodBtn, flex: 1, ...(method === m ? styles.payMethodActive : {}), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                    onClick={() => { setMethod(m); setPayError(null); }}>
                    <Icon name="waves" size={15} style={{ color: m === 'wave' ? '#0a84ff' : '#ff6a00' }} />
                    {m === 'wave' ? 'Wave' : 'Orange Money'}
                  </button>
                ))}
              </div>
            </div>

            {payError && (
              <div style={{ background: '#fff0f0', border: '1px solid #ffb3b3', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#cc0000' }}>
                {payError}
              </div>
            )}

            {method === 'wallet' ? (
              <button style={{ ...styles.btnSuccess, opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={payWithWallet} disabled={loading}>
                {loading ? '...' : <><Icon name="check_circle" size={16} fill={1} /> Payer {formatPrice(product?.prix)} depuis mon compte</>}
              </button>
            ) : (
              <button style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={initMobilePayment} disabled={loading}>
                {loading
                  ? 'Initialisation...'
                  : <><Icon name="send" size={16} /> Payer {formatPrice(product?.prix)} via {methodLabel}</>}
              </button>
            )}
            <button style={styles.btnCancel} onClick={onClose}>Annuler</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── DELIVERY PAGE ─────────────────────────────────────────────────────────────
function DeliveryPage({ token }) {
  const [data, setData]           = useState(null);
  const [error, setError]         = useState(null);
  const [starting, setStarting]   = useState(false);
  // codeModal: { userId, orderIds } — which buyer is being validated
  const [codeModal, setCodeModal] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [validating, setValidating] = useState(false);
  // unchecked items per buyer: { [userId]: Set<orderId> }
  const [unchecked, setUnchecked] = useState({});

  const reload = () =>
    axios.get(`${API}/delivery/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Lien invalide ou expiré'));

  useEffect(() => { reload(); }, [token]);

  const startDelivery = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/delivery/${token}/start`);
      await reload();
      toast('Livraisons démarrées ! SMS envoyés aux acheteurs.', 'success');
    } catch (e) { toast(e.response?.data?.error || 'Erreur', 'error'); }
    setStarting(false);
  };

  const openCodeModal = (userId, orderIds) => {
    setCodeModal({ userId, orderIds });
    setCodeInput('');
  };

  const submitCode = async () => {
    if (!codeModal) return;
    setValidating(true);
    try {
      const { userId, orderIds } = codeModal;
      await axios.post(`${API}/delivery/${token}/buyers/${userId}/validate`, { code: codeInput, orderIds });
      setData(d => ({
        ...d,
        orders: d.orders.map(o =>
          orderIds.includes(o.id) ? { ...o, deliveryStatus: 'delivered' } : o
        ),
      }));
      setCodeModal(null);
      toast('Livraison confirmée !', 'success');
    } catch (e) { toast(e.response?.data?.error || 'Code incorrect', 'error'); }
    setValidating(false);
  };

  const toggleItem = (userId, orderId) => {
    setUnchecked(prev => {
      const set = new Set(prev[userId] || []);
      set.has(orderId) ? set.delete(orderId) : set.add(orderId);
      return { ...prev, [userId]: set };
    });
  };

  if (!data && !error) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#8e8e93' }}>Chargement...</div>;
  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 12 }}>
      <Icon name="cancel" fill={1} size={48} style={{ color: '#ff3b30' }} />
      <div style={{ fontWeight: 700, fontSize: 20 }}>Lien invalide</div>
      <div style={{ color: '#8e8e93' }}>{error}</div>
    </div>
  );

  const { orders, vendor, batch } = data;

  // Group orders by userId
  const buyerMap = {};
  for (const o of orders) {
    if (!buyerMap[o.userId]) buyerMap[o.userId] = { userId: o.userId, userName: o.userName, phone: o.phone, userAddress: o.userAddress, orders: [] };
    buyerMap[o.userId].orders.push(o);
  }
  const buyers = Object.values(buyerMap);
  const pendingBuyers = buyers.filter(b => b.orders.some(o => o.deliveryStatus !== 'delivered'));
  const deliveredBuyers = buyers.filter(b => b.orders.every(o => o.deliveryStatus === 'delivered'));
  const totalPending = orders.filter(o => o.deliveryStatus !== 'delivered').length;
  const totalDelivered = orders.filter(o => o.deliveryStatus === 'delivered').length;

  return (
    <div style={{ background: '#f2f2f7', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid #e5e5ea', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
        <Icon name="local_shipping" size={28} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{vendor.shopName}</div>
          <div style={{ fontSize: 13, color: '#8e8e93' }}>
            {batch.zones?.join(', ')} · {totalPending} à livrer · {totalDelivered} livrées
          </div>
        </div>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560, margin: '0 auto' }}>
        {/* Start delivery button */}
        {!batch.deliveryStarted && (
          <button
            style={{ ...styles.btnPrimary, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 16, padding: '14px 20px' }}
            onClick={startDelivery}
            disabled={starting}
          >
            <Icon name="send" size={20} />
            {starting ? 'Envoi des SMS…' : 'Démarrer les livraisons'}
          </button>
        )}
        {batch.deliveryStarted && totalPending > 0 && (
          <div style={{ background: '#fff8e6', border: '1px solid #ffe4a0', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: '#7a4f00', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="mark_email_read" size={18} />
            SMS envoyés — les acheteurs ont reçu leur code
          </div>
        )}

        {/* Pending buyers */}
        {pendingBuyers.length > 0 && <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8e8e93', textTransform: 'uppercase', marginTop: 4 }}>À livrer ({pendingBuyers.length} acheteurs)</div>
          {pendingBuyers.map(buyer => {
            const pendingOrders = buyer.orders.filter(o => o.deliveryStatus !== 'delivered');
            const unc = unchecked[buyer.userId] || new Set();
            const selectedIds = pendingOrders.filter(o => !unc.has(o.id)).map(o => o.id);
            return (
              <div key={buyer.userId} style={{ background: '#fff', borderRadius: 16, padding: 16, border: '1px solid #e5e5ea', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{buyer.userName}</div>
                <div style={{ color: '#6e6e73', fontSize: 14 }}>{buyer.phone}</div>
                {buyer.userAddress && (
                  <div style={{ marginTop: 6, fontSize: 14 }}>
                    <Icon name="location_on" size={14} /> <strong>{buyer.userAddress.quartier}</strong>, {buyer.userAddress.ville}
                    {buyer.userAddress.details && <div style={{ color: '#6e6e73', fontSize: 13 }}>{buyer.userAddress.details}</div>}
                  </div>
                )}
                {/* Items list with checkboxes */}
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pendingOrders.map(o => (
                    <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 10, background: unc.has(o.id) ? '#f2f2f7' : '#f0faf4', border: `1px solid ${unc.has(o.id) ? '#e5e5ea' : '#b0e8c0'}` }}>
                      <input
                        type="checkbox"
                        checked={!unc.has(o.id)}
                        onChange={() => toggleItem(buyer.userId, o.id)}
                        style={{ width: 18, height: 18, accentColor: '#30c759', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{o.productName}</div>
                        <div style={{ color: '#30c759', fontWeight: 700, fontSize: 13 }}>{formatPrice(o.prix)}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  style={{ ...styles.btnSuccess, marginTop: 12, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: (!batch.deliveryStarted || selectedIds.length === 0) ? 0.5 : 1 }}
                  disabled={!batch.deliveryStarted || selectedIds.length === 0}
                  onClick={() => openCodeModal(buyer.userId, selectedIds)}
                >
                  <Icon name="pin" size={18} /> Marquer livré ({selectedIds.length})
                </button>
              </div>
            );
          })}
        </>}

        {/* Delivered buyers */}
        {deliveredBuyers.length > 0 && <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8e8e93', textTransform: 'uppercase', marginTop: 4 }}>Livrés ({deliveredBuyers.length} acheteurs)</div>
          {deliveredBuyers.map(buyer => (
            <div key={buyer.userId} style={{ background: '#e8f9ee', borderRadius: 14, padding: '14px 16px', border: '1px solid #b0e8c0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="check_circle" fill={1} size={24} style={{ color: '#30c759' }} />
                <div>
                  <div style={{ fontWeight: 700, color: '#1a7a3a' }}>{buyer.userName}</div>
                  <div style={{ fontSize: 13, color: '#1a7a3a' }}>{buyer.orders.length} article{buyer.orders.length > 1 ? 's' : ''}</div>
                </div>
              </div>
            </div>
          ))}
        </>}
      </div>

      {/* Code challenge modal */}
      {codeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <Icon name="lock" fill={1} size={40} style={{ color: '#0a84ff' }} />
              <div style={{ fontWeight: 800, fontSize: 20, marginTop: 10 }}>Code de confirmation</div>
              <div style={{ color: '#8e8e93', fontSize: 14, marginTop: 6 }}>Demandez le code à 6 chiffres à l'acheteur</div>
            </div>
            <input
              type="number"
              inputMode="numeric"
              placeholder="XXXXXX"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.slice(0, 6))}
              style={{ width: '100%', border: '2px solid #0a84ff', borderRadius: 12, padding: '14px 16px', fontSize: 28, fontWeight: 800, textAlign: 'center', letterSpacing: 8, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
              autoFocus
            />
            <button
              style={{ ...styles.btnPrimary, width: '100%', fontSize: 16, padding: '14px 0', opacity: (codeInput.length !== 6 || validating) ? 0.5 : 1 }}
              disabled={codeInput.length !== 6 || validating}
              onClick={submitCode}
            >
              {validating ? 'Vérification…' : 'Confirmer'}
            </button>
            <button style={{ ...styles.btnCancel, width: '100%', marginTop: 10 }} onClick={() => setCodeModal(null)}>Annuler</button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

// ─── TIKTOK HLS PLAYER ────────────────────────────────────────────────────────
const TIKTOK_REFRESH_MS = 30_000; // refresh URL toutes les 30s

function TikTokHlsPlayer({ username }) {
  const videoRef      = useRef(null);
  const hlsRef        = useRef(null);
  const flvRef        = useRef(null);
  const playerModeRef = useRef(null); // 'hls' | 'flv'
  const timerRef      = useRef(null);
  const failCountRef  = useRef(0);
  const latestUrlRef  = useRef(null);
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quality, setQuality] = useState('auto');
  const [qualities, setQualities] = useState({});
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);

  const stopPlayer = () => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    if (flvRef.current) { flvRef.current.destroy(); flvRef.current = null; }
    playerModeRef.current = null;
  };

  const tryPlayVideo = (video) => {
    video.muted = false;
    video.play().then(() => setMuted(false)).catch(() => {
      video.muted = true;
      setMuted(true);
      video.play().catch(() => {});
    });
    setOffline(false);
    setStarted(true);
  };

  const startHls = (hlsUrl) => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;
    stopPlayer();

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true, lowLatencyMode: true,
        backBufferLength: 30, maxBufferLength: 20, maxMaxBufferLength: 40,
        liveSyncDurationCount: 3,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => tryPlayVideo(video));
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        stopPlayer();
        const freshUrl = latestUrlRef.current;
        if (freshUrl) setTimeout(() => startHls(freshUrl), 1000);
        else setOffline(true);
      });
      hlsRef.current = hls;
      playerModeRef.current = 'hls';
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      tryPlayVideo(video);
      playerModeRef.current = 'hls';
    } else {
      setOffline(true);
    }
  };

  const startFlv = (flvUrl) => {
    const video = videoRef.current;
    if (!video || !flvUrl) return;
    if (!flvjs.isSupported()) { setOffline(true); return; }
    stopPlayer();
    const player = flvjs.createPlayer({ type: 'flv', url: flvUrl, isLive: true, hasAudio: true, hasVideo: true });
    player.attachMediaElement(video);
    player.load();
    player.on(flvjs.Events.ERROR, () => { stopPlayer(); setOffline(true); });
    tryPlayVideo(video);
    flvRef.current = player;
    playerModeRef.current = 'flv';
  };

  const checkLive = async (selectedQuality) => {
    try {
      const res = await axios.get(`${API}/tiktok-live/${username}?force=1`);
      const data = res.data;
      setLoading(false);
      if (!data.live || (!data.hlsUrl && !data.flvUrl)) {
        failCountRef.current += 1;
        if (failCountRef.current >= 2) setOffline(true);
        return;
      }
      failCountRef.current = 0;
      setOffline(false);
      setQualities(data.qualities || {});
      const url = (selectedQuality && data.qualities?.[selectedQuality])
        || data.hlsUrl
        || data.flvUrl;
      latestUrlRef.current = url;
      if (!playerModeRef.current) {
        if (data.hlsUrl) startHls((selectedQuality && data.qualities?.[selectedQuality]) || data.hlsUrl);
        else startFlv(url);
      }
    } catch {
      setLoading(false);
      failCountRef.current += 1;
      if (failCountRef.current >= 2) setOffline(true);
    }
  };

  useEffect(() => {
    checkLive(null);
    timerRef.current = setInterval(() => checkLive(quality === 'auto' ? null : quality), TIKTOK_REFRESH_MS);

    // Quand l'utilisateur revient sur la page, recharger le live si le player ne joue plus
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const video = videoRef.current;
      const isStalled = !playerModeRef.current || !video || video.paused || video.readyState < 2;
      if (isStalled) {
        latestUrlRef.current = null;
        stopPlayer();
        checkLive(quality === 'auto' ? null : quality);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      stopPlayer();
    };
  }, [username]);

  const handleQuality = (q) => {
    setQuality(q);
    const url = q === 'auto' ? latestUrlRef.current : qualities[q];
    if (!url) return;
    if (url.includes('.flv')) startFlv(url);
    else startHls(url);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    video.muted = next;
    setMuted(next);
  };

  if (loading) return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#010101' }}>
      <span style={{ fontSize: 48 }}>🎵</span>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>@{username}</div>
      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Connexion au live TikTok...</div>
    </div>
  );

  if (offline) return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#010101' }}>
      <span style={{ fontSize: 48 }}>🎵</span>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>@{username}</div>
      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, textAlign: 'center', padding: '0 20px' }}>Live TikTok hors ligne ou non démarré</div>
      <a href={`https://www.tiktok.com/@${username}/live`} target="_blank" rel="noopener noreferrer"
        style={{ marginTop: 8, padding: '10px 18px', background: '#fe2c55', color: '#fff', borderRadius: 20, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
        Voir sur TikTok
      </a>
    </div>
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <video
        ref={videoRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        autoPlay playsInline muted
      />
      {started && (
        <button onClick={toggleMute} style={{
          position: 'absolute', bottom: Object.keys(qualities).length > 1 ? 52 : 12, right: 12,
          background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%',
          width: 36, height: 36, cursor: 'pointer', color: '#fff', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {muted ? '🔇' : '🔊'}
        </button>
      )}
      {Object.keys(qualities).length > 1 && (
        <div style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {['auto', ...Object.keys(qualities)].map(q => (
            <button key={q} onClick={() => handleQuality(q)} style={{
              padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              background: quality === q ? '#fff' : 'rgba(0,0,0,0.55)', color: quality === q ? '#010101' : '#fff',
            }}>{q}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── VIEWER PAGE ──────────────────────────────────────────────────────────────
function ViewerPage({ user, socket, vendeurId, walletBalance, userSettings, onDirectPaid, onLiveChange }) {
  const [liveSession, setLiveSession] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [shopInfo, setShopInfo] = useState(null);
  const [myOrderId, setMyOrderId] = useState(null);
  const [myExpiresAt, setMyExpiresAt] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paid, setPaid] = useState(false);
  const [notification, setNotification] = useState(null);
  const [wishlist, setWishlist] = useState([]);

  const showNotif = (msg, type = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    axios.get(`${API}/state?vendeur=${vendeurId}`).then(r => {
      if (r.data.liveSession) setLiveSession(r.data.liveSession);
      if (r.data.currentProduct) setCurrentProduct(r.data.currentProduct);
      onLiveChange?.(!!(r.data.liveSession));
    });
    axios.get(`${API}/vendors/${vendeurId}/public`).then(r => setShopInfo(r.data)).catch(() => {});
    if (user) axios.get(`${API}/users/${user.userId}/wishlist`).then(r => setWishlist(r.data)).catch(() => {});
  }, [vendeurId, user?.userId]);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join:live', vendeurId);

    socket.on('state:sync', data => {
      if (data.liveSession) setLiveSession(data.liveSession);
      if (data.currentProduct) setCurrentProduct(data.currentProduct);
    });
    socket.on('live:started', session => { setLiveSession(session); onLiveChange?.(true); showNotif('🔴 Le live a démarré !', 'success'); });
    socket.on('live:stopped', () => { setLiveSession(null); setCurrentProduct(null); onLiveChange?.(false); showNotif('Live terminé', 'info'); });
    socket.on('product:presented', product => {
      setCurrentProduct(product);
      setMyOrderId(null); setMyExpiresAt(null); setPaid(false);
      showNotif(`📦 Nouveau produit : ${product.nom}`, 'info');
    });
    socket.on('product:hidden', () => setCurrentProduct(null));
    socket.on('product:locked', data => setCurrentProduct(p => p ? { ...p, status: 'locked', waitlistCount: data.waitlistCount } : p));
    socket.on('product:sold', data => {
      setCurrentProduct(p => p ? { ...p, status: 'sold' } : p);
      showNotif(`✅ Vendu à ${data.buyerName}`, 'success');
    });
    socket.on('order:expired', () => {
      setCurrentProduct(p => p ? { ...p, status: 'available', lockedBy: null } : p);
      showNotif('⏰ La réservation a expiré — produit à nouveau disponible !', 'warning');
    });
    socket.on('waitlist:updated', data => setCurrentProduct(p => p && p.id === data.productId ? { ...p, waitlistCount: data.count } : p));
    socket.on('waitlist:your_turn', data => {
      if (data.userId === user?.userId) {
        showNotif(`🎯 C'est votre tour ! Vous avez 10 minutes`, 'success');
        setCurrentProduct(p => p ? { ...p, status: 'available', lockedBy: null } : p);
      }
    });

    return () => {
      ['state:sync','live:started','live:stopped','product:presented','product:hidden',
       'product:locked','product:sold','order:expired','waitlist:updated','waitlist:your_turn'
      ].forEach(e => socket.off(e));
    };
  }, [socket, vendeurId, user]);

  const handleBought = (orderId, expiresAt) => {
    setMyOrderId(orderId); setMyExpiresAt(expiresAt); setShowPayment(true);
    setCurrentProduct(p => p ? { ...p, status: 'locked', lockedBy: user?.userId } : p);
  };

  const handlePaid = () => { setShowPayment(false); setPaid(true); showNotif('🎉 Paiement confirmé !', 'success'); };
  const handleDirectPaid = (newBalance) => { setPaid(true); setCurrentProduct(p => p ? { ...p, status: 'sold' } : p); onDirectPaid(newBalance); showNotif('🎉 Paiement direct confirmé depuis votre compte !', 'success'); };
  const handleWalletPaid = (newBalance) => { setShowPayment(false); setPaid(true); onDirectPaid(newBalance); showNotif('🎉 Paiement confirmé depuis votre compte !', 'success'); };

  const notifColors = {
    success: { bg: '#e8f9ee', color: '#1a7a3a', border: '#b0e8c0' },
    warning: { bg: '#fff8e6', color: '#7a4f00', border: '#ffd88a' },
    info:    { bg: '#eef4ff', color: '#1a3a7a', border: '#b0ccf5' },
  };

  return (
    <div style={styles.viewerPage}>
      {notification && (
        <div style={{ ...styles.notif, ...(notifColors[notification.type] || notifColors.info) }}>
          {notification.msg}
        </div>
      )}

      {paid && (
        <div style={{ ...styles.paidBanner, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}><Icon name="celebration" size={20} /> Achat confirmé ! Le vendeur vous contactera au {user?.phone}</div>
      )}

      {!liveSession ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={styles.noLiveIcon}><Icon name="live_tv" size={64} style={{ color: '#8e8e93' }} /></div>
            <h2 style={styles.noLiveTitle}>{shopInfo?.shopName || 'Boutique'}</h2>
            <p style={styles.noLiveSub}>Aucun live en cours. Revenez bientôt !</p>
          </div>
          {wishlist.length > 0 && (
            <div style={{ width: '100%', maxWidth: 480 }}>
              <div style={{ ...styles.sectionTitle, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="shopping_basket" size={16} /> Vos articles en attente</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {wishlist.map((item, i) => (
                  <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '14px 16px', border: '1px solid #e5e5ea', boxShadow: '0 1px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    {item.photo
                      ? <img src={item.photo} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 52, height: 52, borderRadius: 10, background: '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="shopping_bag" size={28} style={{ color: '#8e8e93' }} /></div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{item.productName}</div>
                      <div style={{ fontSize: 13, color: '#8e8e93' }}>{item.shopName}</div>
                      <div style={{ color: '#30c759', fontWeight: 800, fontSize: 16 }}>{formatPrice(item.prix)}</div>
                    </div>
                    {item.liveActive ? (
                      <a href={`?live=${item.vendeurId}`} style={{ ...styles.btnBuy, width: 'auto', padding: '8px 14px', fontSize: 13, textDecoration: 'none', display: 'flex', alignItems: 'center', borderRadius: 10, marginTop: 0 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff3b30', display: 'inline-block', marginRight: 5 }} />Rejoindre
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, color: '#8e8e93', fontWeight: 600, textAlign: 'center', flexShrink: 0 }}>Hors<br/>ligne</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.liveLayout}>
          <div style={styles.playerWrap}>
            <div style={styles.liveTag}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff3b30', display: 'inline-block', marginRight: 5 }} />LIVE</div>
            {liveSession.tiktokLiveUsername ? (
              <TikTokHlsPlayer username={liveSession.tiktokLiveUsername} />
            ) : liveSession.videoId ? (
              <iframe style={styles.player} src={`https://www.youtube.com/embed/${liveSession.videoId}?autoplay=1&mute=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Live YouTube" />
            ) : currentProduct?.photo ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>
                <img src={currentProduct.photo} alt={currentProduct.nom} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            ) : shopInfo?.logo ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>
                <img src={shopInfo.logo} alt={shopInfo.shopName} style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '4px solid rgba(255,255,255,0.15)' }} />
              </div>
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: '#111' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#0a84ff,#30c759)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#fff', fontWeight: 700 }}>
                  {getInitials(shopInfo?.shopName || '?')}
                </div>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, fontWeight: 600, animation: 'liveText 2s ease-in-out infinite' }}>● En direct</span>
              </div>
            )}
          </div>

          {currentProduct ? (
            <>
              <ProductBottomBar product={currentProduct} user={user} vendeurId={vendeurId} onBought={handleBought} onDirectPaid={handleDirectPaid} myOrder={myOrderId} onShowPayment={() => setShowPayment(true)} paid={paid} walletBalance={walletBalance} userSettings={userSettings} />
              {myExpiresAt && !paid && <div style={styles.timerBar}><Timer expiresAt={myExpiresAt} /></div>}
            </>
          ) : (
            <div style={styles.noProductBar}><span style={{ ...styles.noProductBarText, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="visibility" size={18} /> En attente du prochain produit...</span></div>
          )}
        </div>
      )}

      {showPayment && currentProduct && (
        <PaymentModal orderId={myOrderId} product={currentProduct} user={user} vendeurId={vendeurId} expiresAt={myExpiresAt}
          onPaid={handlePaid} onWalletPaid={handleWalletPaid} onClose={() => setShowPayment(false)} walletBalance={walletBalance} />
      )}
    </div>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
// ─── LIVES FEED ───────────────────────────────────────────────────────────────
function LivesFeed() {
  const [lives, setLives] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 5;

  const loadingRef = useRef(false);

  const loadPage = async (p, q) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (q && q.trim()) params.set('q', q.trim());
      const res = await axios.get(`${API}/lives/active?${params}`);
      setLives(prev => p === 0 ? res.data.lives : [...prev, ...res.data.lives]);
      setTotal(res.data.total);
      setPage(p);
    } catch {}
    loadingRef.current = false;
    setLoading(false);
  };

  useEffect(() => { loadPage(0, ''); }, []);

  // Debounce search: reset list and reload from page 0
  useEffect(() => {
    const t = setTimeout(() => {
      loadingRef.current = false; // allow new request after query change
      setLives([]);
      setTotal(null);
      setPage(0);
      loadPage(0, query);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  // Intersection observer: trigger when sentinel (placed before last 2 items) enters viewport
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingRef.current && total !== null && lives.length < total) {
        loadPage(page + 1, query);
      }
    }, { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [lives.length, total, page, query]);

  const emptyState = !loading && lives.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Barre de recherche */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f2f2f7', borderRadius: 12, padding: '8px 12px' }}>
          <Icon name="search" size={18} style={{ color: '#8e8e93', flexShrink: 0 }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Boutique, vendeur, téléphone…"
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 14, color: '#1d1d1f', flex: 1, minWidth: 0 }}
          />
          {query.length > 0 && (
            <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
              <Icon name="cancel" size={16} style={{ color: '#8e8e93' }} />
            </button>
          )}
        </div>
      </div>

      {emptyState ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#8e8e93' }}>
          <div style={{ marginBottom: 12 }}><Icon name="live_tv" size={48} style={{ color: '#8e8e93' }} /></div>
          {query.trim() ? (
            <>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#1d1d1f', marginBottom: 8 }}>Aucun résultat</div>
              <div style={{ fontSize: 14 }}>Aucun live ne correspond à « {query} ».</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#1d1d1f', marginBottom: 8 }}>Aucun live en cours</div>
              <div style={{ fontSize: 14 }}>Revenez plus tard ou utilisez un lien partagé par un vendeur.</div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 16px 24px' }}>
          {lives.map((live, i) => {
            const isBeforeLast2 = i === lives.length - 2;
            return (
              <React.Fragment key={live.vendeurId + live.startedAt}>
                {isBeforeLast2 && <div ref={sentinelRef} />}
                <div
                  style={{ background: '#fff', border: '1px solid #e5e5ea', borderRadius: 16, padding: '16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                  onClick={() => window.location.href = `?live=${live.vendeurId}`}
                >
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {live.logo ? (
                      <img
                        src={live.logo}
                        alt={live.shopName}
                        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <div style={{ ...styles.avatarCircle, width: 48, height: 48, fontSize: 18, background: getAvatarColor(live.shopName) }}>
                        {getInitials(live.shopName)}
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: -2, right: -2, background: '#ff3b30', width: 12, height: 12, borderRadius: '50%', border: '2px solid #fff', animation: 'liveDot 1.2s ease-in-out infinite' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1d1d1f', marginBottom: 2 }}>{live.shopName}</div>
                    <div style={{ fontSize: 13, color: '#8e8e93' }}>par {live.vendeurNom}</div>
                  </div>
                  <div style={{ background: '#ff3b30', color: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 8, letterSpacing: 0.5, flexShrink: 0 }}>
                    EN DIRECT
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {loading && (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#8e8e93', fontSize: 14 }}>Chargement...</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PAYMENT LINK PAGE ─────────────────────────────────────────────────────────
function PaymentLinkPage({ token }) {
  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [paymentMethod, setPaymentMethod] = React.useState(null);
  const [paying, setPaying] = React.useState(false);
  const [paid, setPaid] = React.useState(false);
  const [remaining, setRemaining] = React.useState(0);

  React.useEffect(() => {
    axios.get(`/api/pay/${token}`)
      .then(r => {
        setInfo(r.data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.response?.data?.error || 'Lien invalide ou expiré');
        setLoading(false);
      });
  }, [token]);

  React.useEffect(() => {
    if (!info?.expiresAt) return;
    const tick = () => {
      const left = Math.max(0, new Date(info.expiresAt).getTime() - Date.now());
      setRemaining(left);
      if (left === 0) setError('Le lien de paiement a expiré');
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [info?.expiresAt]);

  const handlePay = async () => {
    if (!paymentMethod) return;
    setPaying(true);
    try {
      const res = await axios.post(`/api/pay/${token}`, { paymentMethod });
      // Rediriger vers la page de paiement Kopar Pay
      window.location.href = res.data.paymentUrl;
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors du paiement');
      setPaying(false);
    }
  };

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const timerColor = remaining < 60000 ? '#ff3b30' : remaining < 120000 ? '#ff9f0a' : '#30c759';

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f2f2f7' }}>
      <div style={{ fontSize: 16, color: '#8e8e93' }}>Chargement...</div>
    </div>
  );

  if (paid) return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f2f2f7', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1d1d1f', marginBottom: 8 }}>Paiement confirmé !</div>
      <div style={{ fontSize: 15, color: '#8e8e93' }}>Votre commande a été validée. Le vendeur vous contactera pour la livraison.</div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f2f2f7', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>⏰</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#ff3b30', marginBottom: 8 }}>Lien expiré</div>
      <div style={{ fontSize: 15, color: '#8e8e93' }}>{error}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f2f2f7', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 480, marginBottom: 16 }}>
        <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#8e8e93', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
          LiveShop
        </div>
      </div>

      {/* Countdown */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '12px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, width: '100%', maxWidth: 480, boxSizing: 'border-box' }}>
        <div style={{ fontSize: 20 }}>⏱</div>
        <div>
          <div style={{ fontSize: 11, color: '#8e8e93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Temps restant</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: timerColor, fontVariantNumeric: 'tabular-nums' }}>
            {mins}:{String(secs).padStart(2, '0')}
          </div>
        </div>
      </div>

      {/* Product card */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 480, boxSizing: 'border-box', marginBottom: 16 }}>
        {info.productPhoto && (
          <img src={info.productPhoto} alt={info.productName}
            style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 12, marginBottom: 14 }} />
        )}
        <div style={{ fontSize: 11, color: '#8e8e93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Produit</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#1d1d1f', marginBottom: 6 }}>{info.productName}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#0a84ff' }}>
          {Number(info.prix).toLocaleString('fr-FR')} FCFA
        </div>
      </div>

      {/* Customer info */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 480, boxSizing: 'border-box', marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#8e8e93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Vos informations</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1d1d1f' }}>{info.customerName}</div>
        <div style={{ fontSize: 14, color: '#6e6e73' }}>{info.phone}</div>
      </div>

      {/* Payment method */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 480, boxSizing: 'border-box', marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#8e8e93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Moyen de paiement</div>
        {['wave', 'orange_money'].map(method => (
          <div
            key={method}
            onClick={() => setPaymentMethod(method)}
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', borderRadius: 12, border: `2px solid ${paymentMethod === method ? '#0a84ff' : '#e5e5ea'}`, marginBottom: 10, cursor: 'pointer', background: paymentMethod === method ? '#f0f8ff' : '#fff', transition: 'all 0.15s' }}
          >
            <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${paymentMethod === method ? '#0a84ff' : '#c7c7cc'}`, background: paymentMethod === method ? '#0a84ff' : 'transparent', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1d1d1f' }}>{method === 'wave' ? 'Wave' : 'Orange Money'}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pay button */}
      <button
        onClick={handlePay}
        disabled={!paymentMethod || paying || remaining === 0}
        style={{ width: '100%', maxWidth: 480, padding: '16px 0', background: paymentMethod && !paying && remaining > 0 ? '#0a84ff' : '#c7c7cc', color: '#fff', border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 700, cursor: paymentMethod && !paying ? 'pointer' : 'default', transition: 'background 0.15s' }}
      >
        {paying ? 'Traitement...' : `Payer ${Number(info.prix).toLocaleString('fr-FR')} FCFA`}
      </button>
      <div style={{ height: 32 }} />
    </div>
  );
}

function HomePage() {
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', paddingTop: 8 }}>
      <div style={{ padding: '20px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 20, color: '#1d1d1f' }}>Lives en cours</div>
          <div style={{ fontSize: 13, color: '#8e8e93', marginTop: 2 }}>Rejoignez un live et achetez en direct</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <div style={{ fontSize: 12, color: '#8e8e93', textAlign: 'right' }}>Vous êtes vendeur ?</div>
          <div style={{ background: '#f2f2f7', border: '1px solid #e5e5ea', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📱</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1d1d1f' }}>App Vendeur</div>
              <div style={{ fontSize: 11, color: '#8e8e93' }}>Installez l'application mobile</div>
            </div>
          </div>
        </div>
      </div>
      <LivesFeed />
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const { liveVendorId, deliveryToken, payToken } = useRoute();

  // Page livreur — rendu indépendant sans auth
  if (deliveryToken) return <DeliveryPage token={deliveryToken} />;

  // Page de paiement — rendu indépendant sans auth
  if (payToken) return <PaymentLinkPage token={payToken} />;

  // Initialisation synchrone depuis localStorage — pas de flash de RegisterModal
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('ls_user');
      if (!saved) return null;
      const userData = JSON.parse(saved);
      return userData?.token ? userData : null;
    } catch { return null; }
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [userSettings, setUserSettings] = useState({ directPayment: false });
  const [showProfile, setShowProfile] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const socketRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);

  useEffect(() => {
    if (liveVendorId) {
      axios.get(`${API}/vendors/${liveVendorId}/public`).then(r => setShopInfo(r.data)).catch(() => {});
      axios.get(`${API}/state?vendeur=${liveVendorId}`).then(r => setIsLive(!!(r.data.liveSession))).catch(() => {});
    }
  }, [liveVendorId]);

  useEffect(() => {
    // Rafraîchir solde et settings en arrière-plan si l'utilisateur est déjà connecté
    if (user) {
      axios.get(`${API}/users/${user.userId}`)
        .then(r => {
          setWalletBalance(r.data.walletBalance || 0);
          if (r.data.settings) setUserSettings(r.data.settings);
        })
        .catch(err => {
          // Ne déconnecter que sur 401 explicite (token invalide/révoqué)
          // Les erreurs réseau ou serveur indisponible ne doivent pas effacer la session
          if (err?.response?.status === 401) {
            localStorage.removeItem('ls_user');
            setUser(null);
          }
        });
    }

    socketRef.current = io(SOCKET_URL);
    socketRef.current.on('connect', () => setSocketReady(true));
    return () => socketRef.current?.disconnect();
  }, []);

  const handleRegister = (userData) => { setUser(userData); setWalletBalance(0); setUserSettings({ directPayment: false }); };

  // ── Page viewer client
  if (liveVendorId) {
    return (
      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f2f2f7', color: '#1d1d1f', fontFamily: "'Urbanist', sans-serif" }}>
        <nav style={{ ...styles.nav, flexShrink: 0, position: 'relative' }}>
          <button style={{ background: 'none', border: 'none', color: '#0a84ff', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 8px', borderRadius: 8, flexShrink: 0 }}
            onClick={() => window.location.href = '/'}>
            <Icon name="arrow_back" size={16} /> Lives
          </button>
          {shopInfo ? (
            <div style={styles.shopNavBrand}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ ...styles.avatarCircle, width: 34, height: 34, fontSize: 13, background: getAvatarColor(shopInfo.shopName) }}>
                  {getInitials(shopInfo.shopName)}
                </div>
                <div style={{ ...styles.liveDot, background: isLive ? '#30c759' : '#ff3b30', animation: isLive ? 'liveDot 1.2s ease-in-out infinite' : 'none' }} />
              </div>
              <span style={styles.shopNavName}>{shopInfo.shopName}</span>
            </div>
          ) : (
            <div style={{ ...styles.navLogo, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="bolt" fill={1} size={22} style={{ color: '#ff9f0a' }} /> LiveShop</div>
          )}
          {user && <UserAvatar user={user} walletBalance={walletBalance} onClick={() => setShowProfile(true)} />}
        </nav>
        {!user && <RegisterModal onRegister={handleRegister} />}
        {socketReady && (
          <ViewerPage user={user} socket={socketRef.current} vendeurId={liveVendorId}
            walletBalance={walletBalance} userSettings={userSettings} onDirectPaid={setWalletBalance}
            onLiveChange={setIsLive} />
        )}
        {showProfile && user && (
          <ProfilePanel user={user} walletBalance={walletBalance} onClose={() => setShowProfile(false)}
            onWalletUpdate={setWalletBalance} onSettingsUpdate={setUserSettings} />
        )}
        <ToastContainer />
      </div>
    );
  }

  // ── Page d'accueil
  return (
    <div style={styles.app}>
      <nav style={styles.nav}>
        <div style={{ ...styles.navLogo, display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="bolt" fill={1} size={22} style={{ color: '#ff9f0a' }} /> LiveShop</div>
        {user && <UserAvatar user={user} walletBalance={walletBalance} onClick={() => setShowProfile(true)} />}
      </nav>
      <HomePage />
      {showProfile && user && (
        <ProfilePanel user={user} walletBalance={walletBalance} onClose={() => setShowProfile(false)}
          onWalletUpdate={setWalletBalance} onSettingsUpdate={setUserSettings} />
      )}
      <ToastContainer />
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = {
  app: { minHeight: '100vh', background: '#f2f2f7', color: '#1d1d1f', fontFamily: "'Urbanist', sans-serif" },
  nav: { display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#ffffff', borderBottom: '1px solid #e5e5ea', gap: 16, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  navLogo: { fontWeight: 800, fontSize: 20, color: '#1d1d1f', letterSpacing: -0.5 },
  shopNavBrand: { display: 'flex', alignItems: 'center', gap: 10 },
  shopNavName: { fontWeight: 700, fontSize: 16, color: '#1d1d1f' },
  liveDot: { position: 'absolute', bottom: -1, right: -1, width: 11, height: 11, borderRadius: '50%', border: '2px solid #fff' },

  avatarBtn: { display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 10, marginLeft: 'auto' },
  avatarCircle: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 },
  avatarBadge: { fontSize: 13, fontWeight: 600, color: '#30c759', background: '#e8f9ee', padding: '3px 8px', borderRadius: 8 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)' },
  modal: { background: '#ffffff', border: '1px solid #e5e5ea', borderRadius: 20, padding: 36, width: '90%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.12)' },
  modalLogo: { fontSize: 48, textAlign: 'center' },
  modalTitle: { margin: 0, fontSize: 26, fontWeight: 800, textAlign: 'center', color: '#1d1d1f' },
  modalSub: { margin: 0, color: '#6e6e73', textAlign: 'center', fontSize: 15 },

  input: { padding: '13px 16px', borderRadius: 12, border: '1px solid #d2d2d7', background: '#f2f2f7', color: '#1d1d1f', fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box' },

  btnPrimary: { padding: '14px 20px', borderRadius: 12, border: 'none', background: '#0a84ff', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%' },
  btnSuccess: { padding: '14px 20px', borderRadius: 12, border: 'none', background: '#30c759', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%' },
  btnDanger: { padding: '14px 20px', borderRadius: 12, border: 'none', background: '#ff3b30', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  btnCancel: { padding: '10px 16px', borderRadius: 10, border: '1px solid #d2d2d7', background: 'transparent', color: '#6e6e73', fontWeight: 500, fontSize: 14, cursor: 'pointer', marginTop: 8, width: '100%' },
  btnBuy: { width: '100%', padding: '15px 0', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #ff9f0a, #ff3b30)', color: '#fff', fontWeight: 800, fontSize: 16, cursor: 'pointer', letterSpacing: 0.3, boxShadow: '0 4px 20px rgba(255,60,0,0.25)', marginTop: 4 },
  btnWishlist: { width: '100%', padding: '13px 0', borderRadius: 12, border: '1px solid #d2d2d7', background: '#f2f2f7', color: '#1d1d1f', fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 4 },
  btnLink: { background: 'none', border: 'none', color: '#0a84ff', fontWeight: 600, fontSize: 14, cursor: 'pointer', textAlign: 'center', padding: '8px 0' },

  // Profile Panel
  profileOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1500, backdropFilter: 'blur(4px)' },
  profilePanel: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.12)', zIndex: 1600, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.25s ease' },
  profileHeader: { display: 'flex', alignItems: 'center', gap: 14, padding: '20px 20px 16px', borderBottom: '1px solid #e5e5ea', background: '#f9f9fb' },
  profileName: { fontWeight: 700, fontSize: 17, color: '#1d1d1f' },
  profilePhone: { fontSize: 14, color: '#6e6e73', marginTop: 2 },
  profileClose: { marginLeft: 'auto', background: '#f2f2f7', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 14, color: '#6e6e73', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  profileTabs: { display: 'flex', borderBottom: '1px solid #e5e5ea', background: '#fff' },
  profileTab: { flex: 1, padding: '12px 8px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: '#8e8e93', cursor: 'pointer', borderBottom: '2px solid transparent' },
  profileTabActive: { color: '#0a84ff', borderBottom: '2px solid #0a84ff', fontWeight: 700 },
  profileContent: { flex: 1, overflowY: 'auto', padding: 20 },

  walletCard: { background: 'linear-gradient(135deg, #0a84ff, #0060c0)', borderRadius: 16, padding: '24px 20px', textAlign: 'center', color: '#fff' },
  walletLabel: { fontSize: 13, opacity: 0.8, marginBottom: 8 },
  walletAmount: { fontSize: 32, fontWeight: 800, letterSpacing: -1 },
  walletHint: { fontSize: 12, opacity: 0.7, marginTop: 6 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#8e8e93', textTransform: 'uppercase', letterSpacing: 0.5 },
  savedAddress: { background: '#f2f2f7', borderRadius: 12, padding: '12px 14px', marginBottom: 4 },
  settingRow: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '4px 0' },
  settingInfo: { flex: 1 },
  settingTitle: { fontSize: 15, fontWeight: 600, color: '#1d1d1f', marginBottom: 4 },
  settingDesc: { fontSize: 13, color: '#6e6e73', lineHeight: 1.5 },
  settingNote: { background: '#e8f9ee', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#1a7a3a' },
  toggle: { width: 50, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s', padding: 0 },
  toggleThumb: { position: 'absolute', top: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'transform 0.2s' },

  viewerPage: { flex: 1, background: '#f2f2f7', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 },
  liveLayout: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' },
  playerWrap: { flex: 1, position: 'relative', background: '#000', minHeight: 0 },
  player: { width: '100%', height: '100%', border: 'none', display: 'block' },
  liveTag: { position: 'absolute', top: 16, left: 16, background: '#ff3b30', color: '#fff', fontWeight: 700, fontSize: 13, padding: '4px 10px', borderRadius: 6, zIndex: 10, letterSpacing: 1 },

  productBar: { background: '#ffffff', borderTop: '1px solid #e5e5ea', position: 'relative', flexShrink: 0 },
  productBarContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 20px 16px', gap: 6 },
  productBarThumb: { width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e5ea', boxShadow: '0 2px 12px rgba(0,0,0,0.10)' },
  productBarName: { fontSize: 16, fontWeight: 700, color: '#1d1d1f', textAlign: 'center', maxWidth: 340 },
  productBarPrice: { fontSize: 22, fontWeight: 800, color: '#30c759', letterSpacing: -0.5 },
  soldBannerBar: { background: '#30c759', color: '#fff', textAlign: 'center', padding: '6px', fontWeight: 800, fontSize: 13 },
  lockedBannerBar: { background: '#ff9f0a', color: '#fff', textAlign: 'center', padding: '6px', fontWeight: 800, fontSize: 13 },
  wishlistConfirmBar: { background: '#f2f2f7', borderRadius: 10, padding: '8px 14px', fontSize: 13, color: '#6e6e73', textAlign: 'center', maxWidth: 340 },
  timerBar: { background: '#ffffff', borderTop: '1px solid #e5e5ea', padding: '8px 20px', flexShrink: 0 },
  noProductBar: { background: '#ffffff', borderTop: '1px solid #e5e5ea', padding: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  noProductBarText: { color: '#8e8e93', fontSize: 15 },

  noLive: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 16 },
  noLiveIcon: { fontSize: 72 },
  noLiveTitle: { margin: 0, fontSize: 28, fontWeight: 800, color: '#1d1d1f' },
  noLiveSub: { color: '#8e8e93', fontSize: 16 },

  notif: { position: 'fixed', top: 80, right: 20, padding: '14px 20px', borderRadius: 12, fontWeight: 600, fontSize: 15, zIndex: 999, maxWidth: 360, animation: 'slideIn 0.3s ease' },
  paidBanner: { background: '#e8f9ee', border: '1px solid #b0e8c0', color: '#1a7a3a', padding: 14, textAlign: 'center', fontWeight: 600, fontSize: 15, flexShrink: 0 },

  youWon: { background: '#e8f9ee', border: '1px solid #b0e8c0', borderRadius: 10, padding: 12, marginTop: 8, maxWidth: 340 },
  youWonText: { color: '#1a7a3a', fontWeight: 700, fontSize: 15, textAlign: 'center' },
  waitlistBadge: { fontSize: 13, color: '#ff9f0a', marginBottom: 4 },
  soldMsg: { color: '#8e8e93', fontSize: 14, textAlign: 'center', marginTop: 8 },

  timer: { borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  timerLabel: { fontSize: 13 },
  timerValue: { fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },

  payHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0 16px', borderBottom: '1px solid #e5e5ea' },
  payProduct: { fontSize: 16, fontWeight: 700, color: '#1d1d1f' },
  payPrice: { fontSize: 22, fontWeight: 800, color: '#30c759' },
  payInstruction: { color: '#6e6e73', fontSize: 15, margin: '8px 0' },
  payMethods: { display: 'flex', gap: 10, marginBottom: 16 },
  payMethodBtn: { flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #d2d2d7', background: '#f2f2f7', color: '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  payMethodActive: { borderColor: '#0a84ff', color: '#0a84ff', background: '#eef4ff' },
  expiredMsg: { color: '#ff3b30', textAlign: 'center', fontWeight: 600 },

};
